// src/services/templateSchema.js
//
// SourceX Roadmap — PHASE 0: the template schema upgrade.
//
// The v1 template model flattened everything to a label + a scalar text value.
// A real source document needs rich field types, unit toggles, repeatable
// tables, conditional (branching) visibility mirroring eCRF skip logic, visit
// applicability, and source-only blocks (signatures / headers). This module is
// the canonical v2 model plus the pure helpers that operate on it.
//
// DESIGN CONSTRAINTS (these are load-bearing — read before changing):
//
// 1. FRAMEWORK-FREE & PURE. No Firebase, no React, no I/O. Everything here is
//    unit-testable and reusable from Cloud Functions.
//
// 2. ADDITIVE / NORMALIZE-ON-READ. `normalizeTemplate` upgrades a v1 doc in
//    memory on every load. Nothing rewrites stored docs as a migration batch, so
//    an old template or an in-flight patient source doc keeps rendering exactly
//    as before. Unknown keys are preserved, never dropped.
//
// 3. IDEMPOTENT + DETERMINISTIC IDS. v1 has no stable question identity — a
//    question is addressed by (section.name, event.eventName, array index), and
//    `sourcex_patients` docs hold their own SNAPSHOT of the template with
//    answers written into questions[i].value. So a template and a patient
//    snapshot derived from it are two separate docs that must independently
//    normalize to the SAME ids, or every existing patient answer is orphaned.
//    That is why legacy ids are hashed from position+label rather than randomly
//    generated. Do not "improve" this to a UUID for the legacy path.
//
// 4. CODE, NOT AI, FOR ANYTHING DETERMINISTIC (roadmap §5 rule 3). Branching
//    evaluation, filtering, grouping and validation all live here as plain
//    functions. The LLM's job ends at reading the documents.
//
// 5. TEMPLATES ARE BLANK FORMS — THEY MUST NOT CARRY PHI. A template is shared
//    across every patient in a study, so a value typed into it leaks to all of
//    them and lands in a doc with no patient audit trail. `stripAnswers()` is
//    the guard; call it before persisting anything to `sourcex_templates`.

// Bump when a change requires re-normalizing or invalidates stored shape.
export const TEMPLATE_SCHEMA_VERSION = 2;

// ---------------------------------------------------------------------------
// Field types
// ---------------------------------------------------------------------------
// v1 types (must keep their exact spelling — stored docs contain these strings
// and both renderers switch on them lowercased).
export const LEGACY_FIELD_TYPES = Object.freeze([
  "Text",
  "Number",
  "Date",
  "DateTime",
  "Time",
  "YesNo/Maybe",
  "Checkbox",
  "Select",
]);

// v2 additions required by the roadmap's §4 schema requirements.
export const V2_FIELD_TYPES = Object.freeze([
  "TextArea", // free text, multi-line (comments, verbatim terms)
  "MultiSelect", // multi-select checklist (symptom lists, AE checkboxes)
  "Table", // repeatable rows: med history, AEs, conmeds, per-system PE
  "Signature", // source-only attestation / signature block
  "Header", // source-only header identifier (protocol #, site, PI, ...)
]);

export const FIELD_TYPES = Object.freeze([
  ...LEGACY_FIELD_TYPES,
  ...V2_FIELD_TYPES,
]);

// Source-only types are added by the assembly step (roadmap Phase 3); they are
// never produced by parsing an eCRF or SoA, and they hold no clinical answer.
export const SOURCE_ONLY_TYPES = Object.freeze(["Signature", "Header"]);

export const isSourceOnlyType = (type) =>
  SOURCE_ONLY_TYPES.includes(normFieldType(type));

/**
 * Normalize any model/legacy field-type string onto a canonical FIELD_TYPES
 * member. Superset of the v1 normalizer in protocolTypes.js — every input that
 * mapped to X before still maps to X, so re-normalizing stored docs is a no-op.
 * @param {*} t
 * @returns {string}
 */
export const normFieldType = (t) => {
  // The slash matters: "YesNo/Maybe" is itself a member of FIELD_TYPES, and
  // without stripping it the string reduced to "yesno/maybe", missed the
  // "yesnomaybe" key below, and fell through to the `|| "Text"` default. A
  // canonical type has to normalise to itself — picking Yes / No / N/A in the
  // editor silently saved a plain text box instead. It also means an input
  // written "yes/no" or "date/time" now lands where a reader would expect.
  const s = String(t || "")
    .trim()
    .toLowerCase()
    .replace(/[\s_\-/]/g, "");
  const map = {
    // --- v1 (unchanged) ---
    text: "Text",
    string: "Text",
    number: "Number",
    numeric: "Number",
    integer: "Number",
    float: "Number",
    decimal: "Number",
    date: "Date",
    datetime: "DateTime",
    datetimelocal: "DateTime",
    timestamp: "DateTime",
    time: "Time",
    yesno: "YesNo/Maybe",
    yesnomaybe: "YesNo/Maybe",
    ynm: "YesNo/Maybe",
    bool: "Checkbox",
    boolean: "Checkbox",
    checkbox: "Checkbox",
    check: "Checkbox",
    select: "Select",
    dropdown: "Select",
    enum: "Select",
    singleselect: "Select",
    radio: "Select",
    // --- v2 additions ---
    textarea: "TextArea",
    longtext: "TextArea",
    freetext: "TextArea",
    comment: "TextArea",
    multiselect: "MultiSelect",
    checklist: "MultiSelect",
    checkboxes: "MultiSelect",
    multi: "MultiSelect",
    table: "Table",
    grid: "Table",
    repeatable: "Table",
    repeating: "Table",
    log: "Table",
    signature: "Signature",
    sign: "Signature",
    attestation: "Signature",
    header: "Header",
    identifier: "Header",
  };
  return map[s] || "Text";
};

// Which visits/populations a form or question applies to. Mirrors the SoA
// footnote conditions so Phase 3 assembly can filter deterministically.
export const APPLIES_TO = Object.freeze([
  "all",
  "adults_only",
  "adolescents_only",
  "optional",
  "unknown",
]);

// ---------------------------------------------------------------------------
// Stable identity
// ---------------------------------------------------------------------------

/**
 * FNV-1a, 32-bit. Non-cryptographic and NOT a security primitive — this only
 * has to be stable and collision-resistant enough to key form fields. Chosen
 * over a random UUID for the legacy path because migrating the same v1 content
 * twice (once in the template, once in a patient's snapshot copy) must yield
 * identical ids. See constraint 3 at the top of this file.
 * @param {string} str
 * @returns {string} 8-char lowercase hex
 */
export const stableHash = (str) => {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    // h * 16777619, kept in 32-bit range without overflowing to float.
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h.toString(16).padStart(8, "0");
};

/**
 * A question label, as plain text, whatever shape it was stored in.
 *
 * A label is normally a string. The SourceX rich-text work (kinjal-sourcex)
 * lets it be an array of styled RUNS instead — `{text, bold?, italic?,
 * underline?, color?, break?}` — and both branches read the SAME Firestore
 * templates, so a label styled over there arrives here as an array. Rendered
 * straight into JSX that is React's "Objects are not valid as a React child
 * (found: object with keys {text, color})", and it takes the whole editor down
 * on whichever visit holds it.
 *
 * So this branch reads that shape TOLERANTLY rather than pretending it can't
 * exist: the styling is dropped (there is no renderer for it here) and the
 * words survive. The run array itself is kept on the question as
 * `questionRuns` (see normalizeQuestion) so a save from here doesn't destroy
 * it. A break run is a line break, the same as it prints.
 *
 * @param {unknown} label
 * @returns {string}
 */
export const labelToPlainText = (label) => {
  if (typeof label === "string") return label;
  if (Array.isArray(label)) {
    return label
      .map((run) => {
        if (typeof run === "string") return run;
        if (!run || typeof run !== "object") return "";
        if (run.break) return "\n";
        return typeof run.text === "string" ? run.text : "";
      })
      .join("");
  }
  // A bare run object, and anything else that isn't text at all.
  if (label && typeof label === "object" && typeof label.text === "string") {
    return label.text;
  }
  return label === undefined || label === null ? "" : String(label);
};

/** Deterministic id for a v1 question being migrated in place. */
export const legacyQuestionId = (sectionName, eventName, index, label) =>
  `q_${stableHash(
    `${sectionName || ""}|${eventName || ""}|${index}|${label || ""}`
  )}`;

export const legacySectionId = (sectionName, index) =>
  `s_${stableHash(`${sectionName || ""}|${index}`)}`;

export const legacyEventId = (sectionName, eventName, index) =>
  `e_${stableHash(`${sectionName || ""}|${eventName || ""}|${index}`)}`;

/**
 * Id for a genuinely NEW node created in the editor. Random is correct here —
 * there is no second copy that has to derive the same value independently.
 */
export const newId = (prefix) => {
  const c = typeof window !== "undefined" && window.crypto ? window.crypto : null;
  const rand =
    c && typeof c.randomUUID === "function"
      ? c.randomUUID().replace(/-/g, "").slice(0, 12)
      : `${Date.now().toString(36)}${Math.floor(Math.random() * 1e8).toString(36)}`;
  return `${prefix}_${rand}`;
};

export const newQuestionId = () => newId("q");
export const newSectionId = () => newId("s");
export const newEventId = () => newId("e");
export const newColumnId = () => newId("c");

// ---------------------------------------------------------------------------
// Branching / conditional visibility
// ---------------------------------------------------------------------------
// A question carries an optional `visibleWhen` condition tree, mirroring eCRF
// skip logic ("Q1 = Yes reveals Q2-Q5"). Shapes:
//
//   { questionId, op, value }            -- leaf
//   { all: [cond, ...] }                 -- every child must pass
//   { any: [cond, ...] }                 -- at least one child must pass
//   { not: cond }                        -- negation
//
// Absent/empty condition == always visible. Branching is never flattened away:
// it survives ingestion, the SoA join, and per-visit assembly.

export const CONDITION_OPS = Object.freeze([
  "eq",
  "neq",
  "in",
  "notIn",
  "gt",
  "gte",
  "lt",
  "lte",
  "isBlank",
  "notBlank",
  "contains",
]);

const isBlankValue = (v) =>
  v === undefined ||
  v === null ||
  v === "" ||
  (Array.isArray(v) && v.length === 0);

/**
 * Loose equality matching the rest of this codebase's stringly-typed reality:
 * sheet/legacy data yields "5" and "true" where Firestore yields 5 and true.
 * Same rationale as valuesEqual() in patientAuditService.js.
 */
const looseEq = (a, b) => {
  if (a === b) return true;
  if (isBlankValue(a) && isBlankValue(b)) return true;
  if (isBlankValue(a) || isBlankValue(b)) return false;
  return String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
};

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Does an answer match one expected value?
 *
 * A MultiSelect answer is an ARRAY of the ticked options, so "the answer is
 * Option 1" means Option 1 is one of the ticked boxes — not that the whole
 * array equals the string. Comparing the array directly happens to work when
 * exactly ONE box is ticked (String(["a"]) === "a") and silently fails the
 * moment a second is ticked, which is why a conditional field used to vanish
 * as soon as the user selected a second option. Membership is the correct
 * test for every "is it this value" operator.
 *
 * An EMPTY array falls back to looseEq so "answer is blank" still behaves.
 */
const answerMatches = (actual, expected) => {
  if (Array.isArray(actual) && actual.length > 0) {
    return actual.some((a) => looseEq(a, expected));
  }
  return looseEq(actual, expected);
};

const answerMatchesAny = (actual, expectedList) =>
  expectedList.some((e) => answerMatches(actual, e));

const evalLeaf = (cond, answers) => {
  const actual = answers ? answers[cond.questionId] : undefined;
  const expected = cond.value;
  switch (cond.op) {
    case "eq":
      // A list of expected values on `eq` reads as "any of these" — the same
      // thing `in` means. Accepting it here keeps a hand-edited or
      // AI-generated condition working instead of never matching.
      return Array.isArray(expected)
        ? answerMatchesAny(actual, expected)
        : answerMatches(actual, expected);
    case "neq":
      return Array.isArray(expected)
        ? !answerMatchesAny(actual, expected)
        : !answerMatches(actual, expected);
    case "in":
      return Array.isArray(expected)
        ? answerMatchesAny(actual, expected)
        : answerMatches(actual, expected);
    case "notIn":
      return Array.isArray(expected)
        ? !answerMatchesAny(actual, expected)
        : !answerMatches(actual, expected);
    case "isBlank":
      return isBlankValue(actual);
    case "notBlank":
      return !isBlankValue(actual);
    case "contains":
      if (Array.isArray(expected)) return answerMatchesAny(actual, expected);
      // For MultiSelect / Table answers held as arrays.
      if (Array.isArray(actual)) return actual.some((a) => looseEq(a, expected));
      return String(actual ?? "")
        .toLowerCase()
        .includes(String(expected ?? "").toLowerCase());
    case "gt":
    case "gte":
    case "lt":
    case "lte": {
      const a = toNumber(actual);
      const b = toNumber(expected);
      if (a === null || b === null) return false;
      if (cond.op === "gt") return a > b;
      if (cond.op === "gte") return a >= b;
      if (cond.op === "lt") return a < b;
      return a <= b;
    }
    default:
      // Unknown operator: fail OPEN (stay visible). A hidden field silently
      // drops data from the source document; a spurious visible field is
      // merely noise the coordinator can see and ignore. For a regulated
      // record, visible-but-unnecessary beats invisible-and-missing.
      return true;
  }
};

// Operators that assert "the answer IS this value". Two of these on the same
// controlling question are what a coordinator writes when they mean "either
// one reveals the field".
const POSITIVE_MATCH_OPS = Object.freeze(["eq", "in", "contains"]);

/**
 * Evaluate an ALL group, repairing the one construction that can never be true.
 *
 * "Q = Yes, option A" AND "Q = Yes, option B" is unsatisfiable when Q holds a
 * SINGLE answer — one value cannot equal two different options, so the
 * dependent field would be permanently hidden. That is never what was
 * intended; the intent is "either Yes reveals it". So positive-match leaves on
 * the same single-answer question are OR'd together, while the group as a whole
 * still ANDs across DIFFERENT questions.
 *
 * A multi-answer (array) question keeps strict AND — there "both Option 1 and
 * Option 2 are ticked" is a real, satisfiable requirement, and silently
 * loosening it would change a condition that already works.
 */
const evalAll = (conds, answers) => {
  const positivesByQuestion = new Map();
  const rest = [];

  (Array.isArray(conds) ? conds : []).forEach((c) => {
    const isSingleAnswerPositiveLeaf =
      c &&
      typeof c === "object" &&
      c.questionId &&
      POSITIVE_MATCH_OPS.includes(c.op) &&
      !Array.isArray(answers ? answers[c.questionId] : undefined);
    if (!isSingleAnswerPositiveLeaf) {
      rest.push(c);
      return;
    }
    if (!positivesByQuestion.has(c.questionId)) {
      positivesByQuestion.set(c.questionId, []);
    }
    positivesByQuestion.get(c.questionId).push(c);
  });

  if (!rest.every((c) => evaluateCondition(c, answers))) return false;

  for (const leaves of positivesByQuestion.values()) {
    const ok = leaves.some((c) => evaluateCondition(c, answers));
    if (!ok) return false;
  }
  return true;
};

/**
 * Evaluate a condition tree against a map of answers keyed by question id.
 * Pure and total — never throws on malformed input.
 * @param {object|null|undefined} cond
 * @param {Record<string, any>} answers
 * @returns {boolean}
 */
export const evaluateCondition = (cond, answers) => {
  if (!cond || typeof cond !== "object") return true;
  if (Array.isArray(cond.all)) {
    return evalAll(cond.all, answers);
  }
  if (Array.isArray(cond.any)) {
    return cond.any.length === 0
      ? true
      : cond.any.some((c) => evaluateCondition(c, answers));
  }
  if (cond.not) return !evaluateCondition(cond.not, answers);
  if (!cond.questionId || !cond.op) return true;
  return evalLeaf(cond, answers);
};

/** Is this question currently visible given the answers so far? */
export const isQuestionVisible = (question, answers) =>
  evaluateCondition(question && question.visibleWhen, answers);

/**
 * Build the {questionId: value} answer map an event's branching is evaluated
 * against. Kept separate from rendering so both editors and any export path
 * compute visibility identically.
 * @param {object} event
 * @returns {Record<string, any>}
 */
export const buildAnswerMap = (event) => {
  const out = {};
  (event?.questions || []).forEach((q) => {
    if (q && q.id !== undefined) out[q.id] = q.value;
  });
  return out;
};

/**
 * The questions of an event that should actually be shown/filled, with hidden
 * branches removed. Evaluated iteratively so a chain (Q1 reveals Q2, Q2 reveals
 * Q3) resolves correctly regardless of declaration order, with a bounded number
 * of passes so a circular condition can't hang the UI.
 * @param {object} event
 * @returns {Array} visible questions, in original order
 */
export const visibleQuestions = (event) => {
  const all = (event?.questions || []).filter(Boolean);
  const MAX_PASSES = 10;
  let visibleIds = new Set(all.map((q) => q.id));
  for (let pass = 0; pass < MAX_PASSES; pass += 1) {
    // Hidden questions contribute no answer, so a dependent of a hidden
    // question can't be revealed by it. `current` is a per-iteration binding so
    // the closures below never capture the loop-carried variable.
    const current = visibleIds;
    const answers = {};
    all.forEach((q) => {
      if (current.has(q.id)) answers[q.id] = q.value;
    });
    const next = new Set(
      all.filter((q) => isQuestionVisible(q, answers)).map((q) => q.id)
    );
    const stable =
      next.size === current.size && [...next].every((id) => current.has(id));
    visibleIds = next;
    if (stable) break;
  }
  return all.filter((q) => visibleIds.has(q.id));
};

// ---------------------------------------------------------------------------
// Condition authoring helpers (used by the template editor's rule builder)
// ---------------------------------------------------------------------------
// The editor exposes a flat rule list — "show this field when ANY / ALL of
// these are true" — because that is what a coordinator actually writes. These
// helpers convert between that flat shape and the nested `visibleWhen` tree,
// and describe a tree in plain English. Anything too complex to round-trip
// (nested groups, `not`) is reported as such rather than silently flattened.

// Everyday wording, not database wording — these strings are read by
// coordinators, not engineers, and they are what the rule builder and every
// plain-English summary say out loud.
export const OP_LABELS = Object.freeze({
  eq: "is",
  neq: "is not",
  in: "is any of",
  notIn: "is none of",
  contains: "includes",
  gt: "is more than",
  gte: "is at least",
  lt: "is less than",
  lte: "is at most",
  isBlank: "is left blank",
  notBlank: "has any answer",
});

/** One-line explanation of each operator, shown under the picker. */
export const OP_HINTS = Object.freeze({
  eq: "The answer matches this exactly.",
  neq: "The answer is anything except this.",
  in: "The answer matches any one of the values you pick.",
  notIn: "The answer is none of the values you pick.",
  contains: "The answer contains this (for checklists: this box is ticked).",
  gt: "The number entered is more than this.",
  gte: "The number entered is this or more.",
  lt: "The number entered is less than this.",
  lte: "The number entered is this or less.",
  isBlank: "Nothing has been filled in yet.",
  notBlank: "Anything at all has been filled in.",
});

/** Operators that take no value at all. */
export const VALUELESS_OPS = Object.freeze(["isBlank", "notBlank"]);

/** Operators whose value is a LIST of choices rather than a single one. */
export const MULTI_VALUE_OPS = Object.freeze(["in", "notIn"]);

// "is" and "is any of" are the same question asked about one answer or several,
// so picking a second answer should widen the operator rather than throw the
// first one away. These two maps let the UI (and buildCondition) move between
// them automatically as the number of chosen values changes.
export const PLURAL_OP = Object.freeze({ eq: "in", neq: "notIn" });
export const SINGULAR_OP = Object.freeze({ in: "eq", notIn: "neq" });

/** Does this operator compare against a list of answer choices? */
export const isChoiceOp = (op) =>
  Object.prototype.hasOwnProperty.call(PLURAL_OP, op) ||
  Object.prototype.hasOwnProperty.call(SINGULAR_OP, op);

/** Flatten every leaf condition of a tree, in order. */
export const conditionLeaves = (cond) => {
  const out = [];
  const walk = (c) => {
    if (!c || typeof c !== "object") return;
    if (Array.isArray(c.all)) return c.all.forEach(walk);
    if (Array.isArray(c.any)) return c.any.forEach(walk);
    if (c.not) return walk(c.not);
    if (c.questionId) out.push(c);
  };
  walk(cond);
  return out;
};

/**
 * Read a `visibleWhen` tree into the editor's flat rule model.
 *
 * @param {object|null} cond
 * @returns {{mode: "any"|"all", rules: Array, complex: boolean}}
 *   `complex` means the stored tree is nested or negated and cannot be shown
 *   faithfully as a flat list — the editor surfaces that instead of pretending
 *   to have parsed it, so a hand-authored rule is never quietly rewritten.
 */
export const parseCondition = (cond) => {
  const empty = { mode: "any", rules: [], complex: false };
  if (!cond || typeof cond !== "object") return empty;

  const isFlatGroup = (list) =>
    Array.isArray(list) &&
    list.every(
      (c) =>
        c &&
        typeof c === "object" &&
        !Array.isArray(c.all) &&
        !Array.isArray(c.any) &&
        !c.not &&
        c.questionId
    );

  if (Array.isArray(cond.any)) {
    if (!isFlatGroup(cond.any)) {
      return { mode: "any", rules: conditionLeaves(cond), complex: true };
    }
    return { mode: "any", rules: cond.any.map(normalizeRule), complex: false };
  }
  if (Array.isArray(cond.all)) {
    if (!isFlatGroup(cond.all)) {
      return { mode: "all", rules: conditionLeaves(cond), complex: true };
    }
    return { mode: "all", rules: cond.all.map(normalizeRule), complex: false };
  }
  if (cond.not) {
    return { mode: "any", rules: conditionLeaves(cond), complex: true };
  }
  if (cond.questionId) {
    return { mode: "any", rules: [normalizeRule(cond)], complex: false };
  }
  return empty;
};

const normalizeRule = (c) => ({
  questionId: c.questionId,
  op: CONDITION_OPS.includes(c.op) ? c.op : "eq",
  value: c.value,
});

/**
 * Build a `visibleWhen` tree from the editor's flat rule model. Returns
 * `undefined` for "always visible", so the caller can delete the key rather
 * than store an empty condition (an empty condition and no condition must not
 * be two different states in stored data).
 *
 * @param {"any"|"all"} mode
 * @param {Array} rules
 */
export const buildCondition = (mode, rules) => {
  const clean = (Array.isArray(rules) ? rules : [])
    .filter((r) => r && r.questionId && CONDITION_OPS.includes(r.op))
    .map((r) => {
      const out = { questionId: r.questionId, op: r.op };
      if (VALUELESS_OPS.includes(r.op)) return out;

      const asList = Array.isArray(r.value)
        ? r.value.filter((v) => v !== "" && v !== undefined && v !== null)
        : r.value === "" || r.value === undefined || r.value === null
        ? []
        : [r.value];

      if (MULTI_VALUE_OPS.includes(r.op)) {
        // "is any of" with a single choice left is just "is" — narrow it back so
        // the rule reads naturally, without changing what it matches.
        if (asList.length === 1 && SINGULAR_OP[r.op]) {
          out.op = SINGULAR_OP[r.op];
          out.value = asList[0];
        } else {
          out.value = asList;
        }
        return out;
      }

      // A single-value operator carrying SEVERAL values means the author picked
      // more than one acceptable answer. Widen "is" to "is any of" rather than
      // silently keeping only the first — dropping the rest is how a rule that
      // used to match two answers quietly starts matching one.
      if (asList.length > 1 && PLURAL_OP[r.op]) {
        out.op = PLURAL_OP[r.op];
        out.value = asList;
        return out;
      }

      out.value = asList.length ? asList[0] : "";
      return out;
    })
    // A rule needing a value but carrying none matches nothing useful — drop it
    // rather than persist a rule that permanently hides the field.
    .filter(
      (r) =>
        VALUELESS_OPS.includes(r.op) ||
        (Array.isArray(r.value) ? r.value.length > 0 : r.value !== "")
    );

  if (clean.length === 0) return undefined;
  if (clean.length === 1) return clean[0];
  return mode === "all" ? { all: clean } : { any: clean };
};

/**
 * One rule as plain English, e.g. `Was the subject hospitalized? is Yes`.
 * @param {object} rule
 * @param {Map<string,string>} labelById
 */
export const describeRule = (rule, labelById) => {
  if (!rule || !rule.questionId) return "";
  const label = labelById?.get(rule.questionId) || "a previous answer";
  const op = OP_LABELS[rule.op] || rule.op;
  if (VALUELESS_OPS.includes(rule.op)) return `${label} ${op}`;
  const val = Array.isArray(rule.value)
    ? rule.value.join(" / ")
    : String(rule.value ?? "");
  return `${label} ${op} “${val}”`;
};

/**
 * A whole condition as a structured summary the UI can render with emphasis.
 * @returns {{mode:"any"|"all", parts:string[], complex:boolean}}
 */
export const describeCondition = (cond, labelById) => {
  const { mode, rules, complex } = parseCondition(cond);
  return {
    mode,
    complex,
    parts: rules.map((r) => describeRule(r, labelById)),
  };
};

/**
 * Reverse index: which questions does each CONTROLLING question reveal?
 *
 * The editor shows a conditional field's own rule, but the question doing the
 * controlling gives no hint that anything depends on it — so a coordinator
 * editing its options can break a branch without seeing it. This makes the
 * dependency visible from both ends.
 *
 * @param {object} event
 * @returns {Map<string, Array<{id: string, question: string, rule: object}>>}
 *   controlling question id -> the fields its answer can reveal
 */
export const buildDependentsMap = (event) => {
  const out = new Map();
  (event?.questions || []).forEach((q) => {
    if (!q?.visibleWhen) return;
    conditionLeaves(q.visibleWhen).forEach((rule) => {
      if (!rule.questionId) return;
      if (!out.has(rule.questionId)) out.set(rule.questionId, []);
      const list = out.get(rule.questionId);
      if (!list.some((d) => d.id === q.id && d.rule === rule)) {
        list.push({ id: q.id, question: q.question || "(untitled field)", rule });
      }
    });
  });
  return out;
};

/**
 * Rewrite the VALUES of every rule that points at `questionId`, applying a
 * rename map of old option label -> new option label.
 *
 * Rules store the option's printed text, so renaming a choice in the option
 * editor would otherwise leave every rule matching a label that no longer
 * exists — the field silently stops appearing, with nothing on screen to
 * explain why. Renames are followed automatically instead.
 *
 * @param {object|null} cond
 * @param {string} questionId
 * @param {Map<string,string>} renames
 * @returns {object|null} a new tree (or the original when nothing changed)
 */
export const remapConditionValues = (cond, questionId, renames) => {
  if (!cond || typeof cond !== "object" || !renames || renames.size === 0) {
    return cond;
  }
  const mapOne = (v) => {
    const key = String(v ?? "");
    return renames.has(key) ? renames.get(key) : v;
  };
  const walk = (c) => {
    if (!c || typeof c !== "object") return c;
    if (Array.isArray(c.all)) return { ...c, all: c.all.map(walk) };
    if (Array.isArray(c.any)) return { ...c, any: c.any.map(walk) };
    if (c.not) return { ...c, not: walk(c.not) };
    if (c.questionId !== questionId || c.value === undefined) return c;
    return {
      ...c,
      value: Array.isArray(c.value) ? c.value.map(mapOne) : mapOne(c.value),
    };
  };
  return walk(cond);
};

/**
 * Which fields have a rule that depends on one specific option of one
 * question — so deleting that option can warn about exactly what it breaks
 * instead of quietly disabling a branch.
 *
 * @returns {Array<{id: string, question: string}>}
 */
export const findFieldsUsingOption = (event, questionId, optionValue) => {
  const target = String(optionValue ?? "").trim().toLowerCase();
  const hit = [];
  (event?.questions || []).forEach((q) => {
    if (!q?.visibleWhen) return;
    const uses = conditionLeaves(q.visibleWhen).some((rule) => {
      if (rule.questionId !== questionId || rule.value === undefined) return false;
      const vals = Array.isArray(rule.value) ? rule.value : [rule.value];
      return vals.some((v) => String(v ?? "").trim().toLowerCase() === target);
    });
    if (uses) hit.push({ id: q.id, question: q.question || "(untitled field)" });
  });
  return hit;
};

// ---------------------------------------------------------------------------
// Factories
// ---------------------------------------------------------------------------

export const defaultEventSignatures = () => ({
  PI: { status: "Not Sent", signedBy: "", timestamp: "", reason: "" },
  Coordinator: { status: "Not Sent", signedBy: "", timestamp: "", reason: "" },
});

/** Empty answer appropriate to a field type. */
export const emptyValueFor = (type) => {
  switch (normFieldType(type)) {
    case "MultiSelect":
      return [];
    case "Table":
      return [];
    case "Checkbox":
      return false;
    default:
      return "";
  }
};

/**
 * Create a schema-correct question.
 * @param {object} init
 */
// HOW MUCH TEXT A PREFILLED TABLE CELL MAY HOLD
//
// These two numbers are one contract with functions/eligibilityExtraction.js,
// and they are written down together because the gap between them was a bug.
//
// A prefill was originally one thing: a coordinator's STARTING VALUE, "a
// pre-typed N/A". 300 characters is a generous ceiling for that, and it is
// still the ceiling for it.
//
// The eligibility checklist put something else in the same field. Its criteria
// live in `prefillRows` — that is what makes them survive stripAnswers() — but
// they are the FORM, not a default, and a real exclusion criterion routinely
// runs past 300 characters. So every long criterion was silently chopped
// mid-sentence on the way into Firestore, while the server had already accepted
// it whole: the client was applying a tighter limit than the contract, and
// nothing anywhere said so.
//
// STRUCTURAL_CELL_MAX therefore matches MAX_TEXT in
// functions/eligibilityExtraction.js exactly. If one moves, move the other —
// a client cap below the server's is invisible truncation, and truncation of a
// document a monitor checks against the protocol is a finding.
//
// The larger cap is scoped to `structural` columns (see makeColumn) rather than
// applied to every prefill, because only that flag means "this is the form, not
// an answer".
const PREFILL_CELL_MAX = 300;
const STRUCTURAL_CELL_MAX = 2000;

// Rows in one fixed-row table. Matches MAX_ITEMS per list in
// functions/eligibilityExtraction.js, for the same reason as the caps above:
// this used to be 100 while the server returned up to 200, so a protocol with a
// long exclusion list lost the tail of it with no error anywhere.
const MAX_TABLE_ROWS = 200;

export const makeQuestion = (init = {}) => {
  const type = normFieldType(init.type);
  const q = {
    id: init.id || newQuestionId(),
    // labelToPlainText, not String(): a styled run array stringifies to
    // "[object Object]" and loses the words.
    question: labelToPlainText(init.question).slice(0, 500),
    type,
    value: init.value !== undefined ? init.value : emptyValueFor(type),
    required: init.required === true,
  };
  // Square marks on a pick-one question, where the printed form draws them
  // that way. See markStyleFor — an explicit opt-in, never a default.
  if (init.markStyle === "box" || init.markStyle === "radio") {
    q.markStyle = init.markStyle;
  }
  if (init.sourceRef) q.sourceRef = String(init.sourceRef).slice(0, 400);
  if (init.aiSuggested) q.aiSuggested = true;
  if (init.appliesTo && init.appliesTo !== "all") q.appliesTo = init.appliesTo;
  if (init.visibleWhen) q.visibleWhen = init.visibleWhen;

  // Number: a single unit, or a unit toggle (kg/lbs, °C/°F) with a selection.
  if (type === "Number") {
    if (Array.isArray(init.units) && init.units.length) {
      q.units = init.units.map((u) => String(u).slice(0, 20)).slice(0, 6);
      q.unit = init.unit && q.units.includes(init.unit) ? init.unit : q.units[0];
    } else if (init.unit) {
      q.unit = String(init.unit).slice(0, 40);
    }
  }

  if (type === "Select" || type === "MultiSelect") {
    // No practical cap — a coordinator building a checklist or dropdown gets
    // to add as many choices as the real form needs. 2000 is just a sanity
    // ceiling against a runaway paste, not a real-world limit.
    q.options = (Array.isArray(init.options) ? init.options : [])
      .map((o) => String(o))
      .slice(0, 2000);
  }

  if (type === "Table") {
    q.columns = (Array.isArray(init.columns) ? init.columns : []).map((c) =>
      makeColumn(c)
    );
    q.minRows = Number.isInteger(init.minRows) ? init.minRows : 0;
    q.maxRows = Number.isInteger(init.maxRows) ? init.maxRows : 50;

    // Height of each body row, IN POINTS. A standing paper log is filled in by
    // hand over months, so a one-line row is unusable for a drug name and an
    // indication.
    //
    // Points rather than "lines" because the number that matters is whether the
    // whole log still fits on ONE page — a coordinator prints it once and
    // photocopies it. The log definitions take theirs straight off the measured
    // source PDFs, and a line count can't express 26pt. STRUCTURE like
    // rowLabels, and absent unless set, so every table that has ever existed
    // keeps its 16pt rows.
    const rowHeightPt = Number(init.rowHeightPt);
    if (Number.isFinite(rowHeightPt) && rowHeightPt > 0) {
      q.rowHeightPt = Math.min(Math.round(rowHeightPt), 120);
    }

    // A leading narrow column ("No."). The renderers already draw exactly this
    // column to tie the chunks of a SPLIT wide table together; `numbered` turns
    // it on for a table that isn't being split, which is what a paper log's
    // "No." column is.
    //
    // On a log its cells print EMPTY — the paper form leaves the coordinator to
    // number the entries themselves. The split path still prints 1..N, because
    // there the number is the only thing tying a row across the sub-tables.
    if (init.numbered) {
      q.numbered = true;
      if (init.numberHeader) {
        q.numberHeader = String(init.numberHeader).slice(0, 40);
      }
    }

    // Type size of the column-header row, in points. A standing log's headers
    // ARE the form — they carry the only instructions on the page — so they
    // print larger than the 9pt a visit's small inline grid uses.
    const headerFontPt = Number(init.headerFontPt);
    if (Number.isFinite(headerFontPt) && headerFontPt > 0) {
      q.headerFontPt = Math.min(Math.round(headerFontPt), 24);
    }

    // Fixed row labels — a per-system physical exam, a fixed lab panel. These
    // are STRUCTURE, not answers: they print pre-filled on the blank form and
    // the row set can't be added to or removed from. Keeping them here rather
    // than seeding `value` is what makes them survive stripAnswers(), which
    // blanks every answer on the way into a shared template.
    if (Array.isArray(init.rowLabels) && init.rowLabels.length) {
      q.rowLabels = init.rowLabels
        .map((r) => String(r).slice(0, 200))
        .slice(0, MAX_TABLE_ROWS);
      q.rowLabelHeader = String(init.rowLabelHeader || "").slice(0, 100);

      // How wide that label column is, as a percent of the page.
      //
      // A row-label column normally holds WORDS ("Cardiovascular"), so it takes
      // a full share like every other column — that is the right default and it
      // stays the default. The eligibility checklist's label column holds a
      // NUMBER, and a fifth of the page for "14" is a fifth of the page the
      // criteria text needed. Absent unless asked for.
      const labelPct = Number(init.rowLabelWidthPct);
      if (Number.isFinite(labelPct) && labelPct > 0 && labelPct < 100) {
        q.rowLabelWidthPct = labelPct;
      }
    }

    // Prefilled cell text — a default/example value that prints already on
    // the blank form (e.g. a pre-typed "N/A") rather than a line to write on.
    // Also STRUCTURE, not an answer, for the same stripAnswers() reason as
    // rowLabels above: it lives outside `value` so it survives every write to
    // a shared template. Only meaningful for a structurally-known row count
    // (a fixed-row table, or the single row of a composite question) — an
    // open-ended repeatable table has no particular row to attach a default
    // to, so the editor never offers this for that case. Keyed by column id,
    // one object per row, so renaming a column doesn't orphan its default.
    if (Array.isArray(init.prefillRows) && init.prefillRows.length) {
      const colIds = new Set(q.columns.map((c) => c.id));
      // A default is usually one string, but a MultiSelect (checklist) piece
      // can have SEVERAL pre-checked boxes — its default is a list, not a
      // single value. Preserve that shape rather than collapsing it to one
      // comma-joined string, or a second/third pre-checked box silently
      // disappears the moment this gets normalized.
      // Which columns hold FORM TEXT rather than a starting value — the whole
      // reason the two caps below differ. See PREFILL_CELL_MAX.
      const structuralIds = new Set(
        q.columns.filter((c) => c && c.structural).map((c) => c.id)
      );
      const cleanCellValue = (v, colId) => {
        const max = structuralIds.has(colId)
          ? STRUCTURAL_CELL_MAX
          : PREFILL_CELL_MAX;
        if (Array.isArray(v)) {
          const arr = v
            .map((x) => String(x).slice(0, max))
            .filter(Boolean)
            .slice(0, 200);
          return arr.length ? arr : undefined;
        }
        if (v !== undefined && v !== null && String(v).trim()) {
          return String(v).slice(0, max);
        }
        return undefined;
      };
      const cleaned = init.prefillRows.slice(0, MAX_TABLE_ROWS).map((row) => {
        const r = row && typeof row === "object" ? row : {};
        const out = {};
        for (const [k, v] of Object.entries(r)) {
          if (!colIds.has(k)) continue;
          const cleanedVal = cleanCellValue(v, k);
          if (cleanedVal !== undefined) out[k] = cleanedVal;
        }
        return out;
      });
      if (cleaned.some((r) => Object.keys(r).length)) {
        q.prefillRows = cleaned;
      }
    }

    // Sub-headings INSIDE a table — "Medical Conditions:", "Medications/
    // Treatments:", "Other:" partway down an exclusion-criteria checklist. Each
    // prints as one bold row spanning every column, immediately BEFORE the body
    // row it is keyed to.
    //
    // Keyed by row index rather than being a row of its own on purpose. A
    // heading is not an entry: it takes no number, and it must not shift what
    // `value[i]` and `prefillRows[i]` refer to. Making it a real row would do
    // both — the criteria after it would renumber and every recorded answer
    // below it would slide by one. STRUCTURE, like rowLabels, so it survives
    // stripAnswers(); absent on every table that predates the checklist.
    if (init.groupRows && typeof init.groupRows === "object") {
      const groups = {};
      for (const [k, v] of Object.entries(init.groupRows)) {
        const row = Number(k);
        if (!Number.isInteger(row) || row < 0 || row > 1000) continue;
        const label = String(v == null ? "" : v).trim();
        if (label) groups[row] = label.slice(0, 200);
      }
      if (Object.keys(groups).length) q.groupRows = groups;
    }
  }

  if (type === "Signature") {
    q.signatureRole = String(init.signatureRole || "Coordinator").slice(0, 40);
    q.attestation = String(init.attestation || "").slice(0, 1000);
  }

  // Header sections may carry a short right-aligned instruction that the filed
  // source format prints in red beside the title ("Prior to other testing",
  // "After VS"). Additive and optional — absent on every existing doc.
  if (type === "Header" && init.instruction) {
    q.instruction = String(init.instruction).slice(0, 200);
  }

  // A coordinator-set default for a single (non-Table) question — the exact
  // same idea as a Table's prefillRows above, and for the same reason kept
  // OUTSIDE `value`: `value` is blanked by stripAnswers() on every write to a
  // shared template, so a default living there would vanish the moment the
  // template was next saved. Not meaningful for Table (its own prefillRows
  // already covers this) or a source-only type (no answer to default).
  if (!isSourceOnlyType(type) && type !== "Table" && init.defaultValue !== undefined) {
    // A starting value, so PREFILL_CELL_MAX — the same 300 this has always
    // been, now said by name. A single question has no `structural` notion:
    // there is no form text hiding in here the way there was in prefillRows.
    if (Array.isArray(init.defaultValue)) {
      const arr = init.defaultValue
        .map((x) => String(x).slice(0, PREFILL_CELL_MAX))
        .filter(Boolean)
        .slice(0, 200);
      if (arr.length) q.defaultValue = arr;
    } else if (init.defaultValue !== null && String(init.defaultValue).trim()) {
      q.defaultValue = String(init.defaultValue).slice(0, PREFILL_CELL_MAX);
    }
  }

  return q;
};

/**
 * Every field makeQuestion() decides for itself — it SETS each of these when
 * the question needs it and OMITS it when it doesn't.
 *
 * That distinction is why this list has to exist. Rebuilding an edited question
 * as `{...previous, ...makeQuestion(next)}` looks like it replaces everything,
 * but a key the factory omitted isn't overwritten by the spread — it survives
 * from `previous`. So clearing a starting value, emptying a table's cells, or
 * deleting a Number's unit all appeared to work in the editor and then came
 * back on save, because the factory expressed "no value" as absence and the
 * spread read absence as "unchanged".
 *
 * @see applyQuestionEdit, which is how an edit should be merged.
 */
export const FACTORY_OWNED_QUESTION_FIELDS = Object.freeze([
  "type",
  "question",
  "required",
  "value",
  "options",
  "unit",
  "units",
  "columns",
  "minRows",
  "maxRows",
  "rowLabels",
  "rowLabelHeader",
  "rowLabelWidthPct",
  "prefillRows",
  "groupRows",
  "defaultValue",
  "signatureRole",
  "attestation",
  "instruction",
]);

/**
 * Merge an edited question over the version it replaces.
 *
 * The factory's word is final on everything it manages (so a cleared value
 * stays cleared), while anything it has never heard of — provenance an
 * extraction pipeline attached, a field added by a later migration — is carried
 * across untouched.
 *
 * @param {object} previous  the question as it is stored today
 * @param {object} rebuilt   the same question, freshly through makeQuestion()
 * @returns {object}
 */
export const applyQuestionEdit = (previous = {}, rebuilt = {}) => {
  const carried = { ...previous };
  FACTORY_OWNED_QUESTION_FIELDS.forEach((k) => delete carried[k]);
  return { ...carried, ...rebuilt };
};

// ---------------------------------------------------------------------------
// Printed table cells
// ---------------------------------------------------------------------------
// A table CELL is one line in a grid, so a choice column can only print its
// options as tick boxes when the list is short — a Yes/No pair, a PO/IV/Other
// route. A long coded list (the AE log's six "action taken" codes) stays a
// write-in cell, which is how the paper form works: the legend sits under the
// table and the coordinator writes the code. Both exporters share this rule so
// the Word file and the print view can't disagree.
export const CELL_BOX_MAX_OPTIONS = 3;
export const CELL_BOX_MAX_LABEL = 16;

/**
 * A column's fixed answer choices, if it has any — Select/MultiSelect read
 * their own `options`; YesNo/Maybe always has the same three regardless of
 * `options` (it never has any, since makeColumn only sets `options` for
 * Select/MultiSelect). Centralized so a column's choices are computed the
 * same way everywhere a table cell is drawn, rather than each caller
 * re-checking `column.options` and silently missing YesNo/Maybe.
 * @param {object} column
 * @returns {string[]}
 */
export const columnChoices = (column) => {
  const type = normFieldType(column?.type);
  if (type === "Select" || type === "MultiSelect") {
    return Array.isArray(column?.options) ? column.options.map(String) : [];
  }
  if (type === "YesNo/Maybe") return ["Yes", "No", "N/A"];
  return [];
};

/**
 * Should this table column print its options as tick boxes in a cell?
 * @param {object} column
 * @returns {boolean}
 */
export const columnPrintsBoxes = (column) => {
  const choices = columnChoices(column);
  return (
    choices.length > 0 &&
    choices.length <= CELL_BOX_MAX_OPTIONS &&
    choices.every((o) => String(o).length <= CELL_BOX_MAX_LABEL)
  );
};

/** A column that is a single standalone tick box (no option list). */
export const columnIsCheckbox = (column) =>
  normFieldType(column?.type) === "Checkbox";

/**
 * How a choice list's marks are drawn: ROUND for a pick-one list, SQUARE for a
 * tick-any list. This is the paper convention every CRF uses, and it is the
 * only thing on a printed form that tells a coordinator whether they may mark
 * more than one — a dropdown drawn with tick boxes reads as "check all that
 * apply" and gets filled in that way.
 *
 * Centralised so the editor preview, the HTML export and the Word export can't
 * drift: all three ask this, none of them decide it.
 *
 * @param {string} type  a field type (or a table column's type)
 * @returns {"radio"|"box"}
 */
export const choiceMarkFor = (type) =>
  normFieldType(type) === "Select" ? "radio" : "box";

/**
 * A "composite question" — several different answer pieces bundled under one
 * question by the field-type palette — is stored as an ordinary Table with
 * exactly one row and no row labels. It's still a real Table (so every
 * existing renderer already handles it), but it reads better printed as a
 * stack of labeled lines ("Weight: ___  Height: ___") than as a one-row grid
 * with column headers. Every table renderer branches on this the same way, so
 * it's centralized here rather than re-derived per file.
 * @param {object} question
 * @returns {boolean}
 */
export const isCompositeTable = (question) =>
  normFieldType(question?.type) === "Table" &&
  question?.minRows === 1 &&
  question?.maxRows === 1 &&
  !(Array.isArray(question?.rowLabels) && question.rowLabels.length > 0);

/**
 * How many rows a BLANK table prints — fixed row labels, else honour minRows,
 * and never more than maxRows. minRows is a real, user-set choice (the
 * question builder's "rows to print" control), so it's respected exactly,
 * including a value under 4 — 4 is only the fallback for a question with no
 * minRows set at all (an older template, or a composite question, though
 * isCompositeTable questions never reach this: the caller checks that first).
 * @param {object} question
 * @returns {number}
 */
export const tablePrintRowCount = (question) => {
  const rowLabels = Array.isArray(question?.rowLabels) ? question.rowLabels : [];
  if (rowLabels.length) return rowLabels.length;
  const minRows =
    Number.isInteger(question?.minRows) && question.minRows > 0 ? question.minRows : 4;
  const maxRows = Number.isInteger(question?.maxRows) ? question.maxRows : Infinity;
  return Math.min(maxRows, minRows);
};

/** One line of writing space: what a table row has always been. */
export const DEFAULT_ROW_HEIGHT_PT = 16;

/**
 * Height of each body row of a blank table, in points. 16 — one line, what
 * every table has always had — unless the table asks for more. A standing paper
 * log filled in by hand needs room for a drug name and an indication, where a
 * visit's small grid does not.
 * @param {object} question
 * @returns {number}
 */
export const tableRowHeightPt = (question) => {
  const n = Number(question?.rowHeightPt);
  return Number.isFinite(n) && n > 0
    ? Math.min(Math.round(n), 120)
    : DEFAULT_ROW_HEIGHT_PT;
};

/**
 * Per-column width shares, or null to divide the table evenly.
 *
 * All-or-nothing on purpose: a table where only some columns carry a `width`
 * has no sensible reading (what does the rest of the row do?), and silently
 * guessing one would lay the printed form out differently from the .docx. So
 * unless EVERY column is sized, both renderers fall back to the equal shares
 * they have always used.
 *
 * Returned normalized to sum to 100, so a list that sums to 99 or 101 still
 * fills the page exactly rather than leaving a sliver or overflowing.
 *
 * @param {Array<object>} columns
 * @returns {number[]|null}
 */
export const columnWidthPercents = (columns) => {
  const cols = Array.isArray(columns) ? columns : [];
  if (!cols.length) return null;
  const widths = cols.map((c) => Number(c?.width));
  if (!widths.every((w) => Number.isFinite(w) && w > 0)) return null;
  const total = widths.reduce((a, b) => a + b, 0);
  if (!total) return null;
  return widths.map((w) => (w / total) * 100);
};

/**
 * Is this event a standing LOG rather than a study visit? See makeEvent — the
 * flag lives on the event because the event is what the exporters are handed.
 */
export const eventIsLog = (event) => event?.kind === "log";

/**
 * Is this event the study's INCLUSION/EXCLUSION CRITERIA checklist?
 *
 * A third kind of page, sharing one half of a log's chrome and one half of a
 * visit's. Like a log it belongs to the whole study rather than to one visit, so
 * its header carries no "Visit:" and no "Visit Date:". Unlike a log it is a
 * FINISHED document of known length — nobody photocopies a spare page of
 * eligibility criteria — so its footer prints a live "Page 2 of 3" the way a
 * visit's does, and it ends with the screening/final-eligibility sign-off block
 * rather than a log's single line.
 *
 * Deliberately NOT folded into eventIsLog: every existing caller of that means
 * "landscape, ongoing, photocopied", and a checklist is none of those.
 */
export const eventIsCriteria = (event) => event?.kind === "criteria";

/** Does this event print on a landscape page? */
export const eventIsLandscape = (event) => event?.orientation === "landscape";

/**
 * How many columns fit across a page before a table has to be SPLIT into
 * stacked sub-tables to stay writable.
 *
 * Portrait's 8 is unchanged — it is the number both exporters have always
 * used. Landscape has 9.9in of content width against portrait's 7.4in, so it
 * carries proportionally more before a column gets too narrow to write a date
 * into. Shared from here so the two exporters cannot drift on it, which is the
 * same reason columnPrintsBoxes and choiceMarkFor live in this file.
 *
 * @param {boolean} landscape
 * @returns {number}
 */
export const splitThresholdFor = (landscape) => (landscape ? 12 : 8);

/** The recorded value of a choice cell, as a list of selected option strings. */
export const cellSelection = (raw) => {
  if (Array.isArray(raw)) return raw.map((v) => String(v));
  if (raw === undefined || raw === null || raw === "") return [];
  return [String(raw)];
};

/**
 * Does this stored value mean a tick box is ticked?
 *
 * A Checkbox prints as ONE bare box now (it used to print "☐ Yes  ☐ No", which
 * is a different question). That means values written under the old rendering
 * have to keep working: a pre-check saved back then reads "Yes", one saved by
 * the current editor reads "Checked", and a patient's recorded answer is a real
 * boolean. All three tick the same box. One predicate so the preview, the
 * print view and the Word file cannot disagree about a ticked box.
 */
export const checkboxIsTicked = (raw) => {
  const vals = cellSelection(raw).map((v) => v.trim().toLowerCase());
  return vals.some((v) => v === "true" || v === "yes" || v === "checked");
};

/** A column of a repeatable Table question. */
/**
 * The questions a heading owns: the heading itself and everything under it up
 * to the next heading. PURE.
 *
 * Same block boundary the editor's move and delete already use, so "this
 * section" means one thing everywhere.
 *
 * @param {object[]} questions one event's questions
 * @param {number} headingIndex position of the heading
 * @returns {object[]} the block, heading first; empty if the index is not a heading
 */
export const headingBlockAt = (questions, headingIndex) => {
  const qs = Array.isArray(questions) ? questions : [];
  const i = Number(headingIndex);
  if (!Number.isInteger(i) || i < 0 || i >= qs.length) return [];
  if (qs[i]?.type !== "Header") return [];
  let end = i + 1;
  while (end < qs.length && qs[end].type !== "Header") end += 1;
  return qs.slice(i, end);
};

/**
 * Prepare a heading block to be dropped into a DIFFERENT event. PURE.
 *
 * Fixing a mis-extracted section in one visit and then repeating that work in
 * the other fourteen is the kind of thing software should do, so a block can be
 * copied across. Two things have to happen for the copy to behave:
 *
 * 1. FRESH IDS. Answers are keyed by question id within an event. Ids repeated
 *    across events collide with nothing today, but reusing them would make two
 *    visits' questions indistinguishable in any later per-id lookup, so each
 *    copy gets its own identity.
 *
 * 2. BRANCHES REPOINTED OR DROPPED. A rule pointing at another question INSIDE
 *    the block is remapped to that question's new id. A rule pointing OUTSIDE
 *    the block refers to something that does not exist in the target visit —
 *    left alone it would never evaluate true and the question would silently
 *    never appear. Those are dropped and reported, so the caller can say so
 *    rather than the coordinator discovering it later.
 *
 * @param {object[]} block from headingBlockAt
 * @returns {{questions: object[], droppedBranches: string[]}}
 */
export const cloneBlockForEvent = (block) => {
  const src = Array.isArray(block) ? block : [];
  if (!src.length) return { questions: [], droppedBranches: [] };

  const idMap = new Map();
  src.forEach((q) => {
    if (q && q.id) idMap.set(q.id, newId(q.type === "Header" ? "h" : "q"));
  });

  const droppedBranches = [];

  const remap = (cond, ownerLabel) => {
    if (!cond || typeof cond !== "object") return { cond, ok: true };
    if (Array.isArray(cond.all)) {
      const parts = cond.all.map((c) => remap(c, ownerLabel));
      return { cond: { ...cond, all: parts.map((p) => p.cond) }, ok: parts.every((p) => p.ok) };
    }
    if (Array.isArray(cond.any)) {
      const parts = cond.any.map((c) => remap(c, ownerLabel));
      return { cond: { ...cond, any: parts.map((p) => p.cond) }, ok: parts.every((p) => p.ok) };
    }
    if (cond.not) {
      const p = remap(cond.not, ownerLabel);
      return { cond: { ...cond, not: p.cond }, ok: p.ok };
    }
    if (!cond.questionId) return { cond, ok: true };
    if (idMap.has(cond.questionId)) {
      return { cond: { ...cond, questionId: idMap.get(cond.questionId) }, ok: true };
    }
    return { cond, ok: false }; // points outside the block
  };

  const questions = src.map((q) => {
    const next = { ...q, id: idMap.get(q.id) || newId(q.type === "Header" ? "h" : "q") };
    if (q.visibleWhen) {
      const label = q.question || "a question";
      const { cond, ok } = remap(q.visibleWhen, label);
      if (ok) next.visibleWhen = cond;
      else {
        delete next.visibleWhen;
        droppedBranches.push(label);
      }
    }
    return next;
  });

  return { questions, droppedBranches };
};

/**
 * Copy ONE question (not a heading), for the editor's "Copy question" (2026-10-29). PURE.
 *
 * A fresh id, and a deep copy so the copy's choices/columns/units are its own
 * arrays, not the original's. Its show/hide rule:
 *   - same visit: kept as is, because the question it points at is right there;
 *   - another visit: dropped and reported, like cloneBlockForEvent, because the
 *     question it points at does not exist there (left alone, the copy would
 *     silently never show).
 *
 * @param {object} question
 * @param {{sameEvent?: boolean}} [opts]
 * @returns {{question: object|null, droppedBranch: string}} droppedBranch is the
 *   question's label when its rule was removed, else ""
 */
export const cloneQuestionForEvent = (question, { sameEvent = false } = {}) => {
  if (!question || typeof question !== "object") return { question: null, droppedBranch: "" };
  // Template questions are plain JSON (they're stored in Firestore as such).
  const copy = JSON.parse(JSON.stringify(question));
  copy.id = newId("q");
  let droppedBranch = "";
  if (copy.visibleWhen && !sameEvent) {
    delete copy.visibleWhen;
    droppedBranch = question.question || "a question";
  }
  return { question: copy, droppedBranch };
};

/**
 * Where a copied question can go in one visit: one place per heading (the end
 * of that heading's block), plus the questions above the first heading when
 * there are any (or the whole visit when it has no headings). PURE.
 *
 * In the question's OWN visit (`sourceIndex` given), the place holding the
 * original puts the copy directly below it rather than at the end.
 *
 * @param {object[]} questions the target visit's questions
 * @param {{sourceIndex?: number}} [opts]
 * @returns {{key: string, label: string, insertAt: number, holdsOriginal: boolean}[]}
 */
export const questionCopyTargets = (questions, { sourceIndex } = {}) => {
  const qs = Array.isArray(questions) ? questions : [];
  const headingAt = qs.map((q, i) => (q?.type === "Header" ? i : -1)).filter((i) => i >= 0);
  const hasSource = Number.isInteger(sourceIndex) && sourceIndex >= 0 && sourceIndex < qs.length;
  const place = (key, label, start, end) => {
    const holdsOriginal = hasSource && sourceIndex >= start && sourceIndex < end;
    return { key, label, insertAt: holdsOriginal ? sourceIndex + 1 : end, holdsOriginal };
  };

  const out = [];
  const firstHeading = headingAt.length ? headingAt[0] : qs.length;
  if (!headingAt.length) {
    out.push(place("__top", qs.length ? "This visit (it has no headings)" : "This visit (it has no questions yet)", 0, qs.length));
  } else if (firstHeading > 0) {
    out.push(place("__top", "Above the first heading", 0, firstHeading));
  }
  headingAt.forEach((h, n) => {
    const end = n + 1 < headingAt.length ? headingAt[n + 1] : qs.length;
    out.push(place(qs[h].id || `__h${h}`, qs[h].question || "Untitled heading", h, end));
  });
  return out;
};

/**
 * Turn a Table back into the separate questions it should have been.
 *
 * WHY THIS EXISTS. The eCRF reader tags a field with `group_name` when it looks
 * like a column of a repeating log, and assembly then collapses every field
 * sharing that group into ONE Table. It over-fires: a question with a codelist
 * — "Was study drug administered? Yes (Y) / No (N)" followed by "If No, what
 * was the reason?" — reads, as plain text, exactly like a header with rows
 * beneath it. What comes out is a table whose columns are the questions, which
 * is not what the sponsor's form asks for.
 *
 * A Table is a single question, and the editor deliberately excludes Tables from
 * the field editor, so an affected source document could not be corrected at all
 * from the UI. This is that correction.
 *
 * The transform is faithful rather than clever: a column already carries the
 * label, type, options and unit a question needs, so each column becomes one
 * question in place, keeping order.
 *
 * BRANCHING. A table carries at most one visibility rule for the whole thing,
 * so every question inherits it — behaviour is preserved. Anything that pointed
 * a branch AT the table is re-pointed at the FIRST question, which is nearly
 * always the controlling one ("Was study drug administered?" precedes "If No,
 * why not?"). Left alone, those references would dangle.
 *
 * @param {object} q a Table question
 * @returns {{questions: object[], controllingId: string}|null} null if `q` is
 *          not a Table, or carries no columns to split.
 */
export const splitTableIntoQuestions = (q) => {
  if (!q || q.type !== "Table") return null;
  const cols = Array.isArray(q.columns) ? q.columns : [];
  if (!cols.length) return null;

  // A column is legal in shapes a QUESTION is not, and splitting must not hand
  // the coordinator a screenful of red to clear up as the price of using it.
  // Two cases, both fixed here rather than left for them to fix by hand:
  //
  //  • A choice column with no options. Inside a table that prints as a blank
  //    cell to write in, so it becomes a write-in question — which is what it
  //    already was on paper. As a Select it would be "has no options", flagged
  //    on every such column.
  //  • A column with no header of its own. It takes the table's name, which is
  //    the only name that column was ever printed under. (If the table has no
  //    name either, the blank stands — that was already flagged on the table
  //    before the split, so nothing new appears.)
  const tableLabel = String(q.question || "").trim();
  const questions = cols.map((c) => {
    const isChoice = c.type === "Select" || c.type === "MultiSelect";
    const options = (Array.isArray(c.options) ? c.options : []).filter((o) =>
      String(o).trim()
    );
    const type = isChoice && !options.length ? "Text" : c.type;
    return makeQuestion({
      question: String(c.label || "").trim() || tableLabel,
      type,
      ...(options.length ? { options } : {}),
      ...(c.unit ? { unit: c.unit } : {}),
      required: q.required === true,
      ...(q.sourceRef ? { sourceRef: q.sourceRef } : {}),
      ...(q.appliesTo ? { appliesTo: q.appliesTo } : {}),
      ...(q.visibleWhen ? { visibleWhen: q.visibleWhen } : {}),
    });
  });

  return { questions, controllingId: questions[0].id };
};

/**
 * Re-point every branch reference from one question id to another. PURE.
 *
 * Walks the whole condition tree — leaves plus all/any/not nodes — because a
 * rule can nest arbitrarily and a missed reference is a branch that silently
 * never fires.
 *
 * @param {object[]} questions
 * @param {string} fromId
 * @param {string} toId
 * @returns {object[]} new array; questions without a matching reference are
 *          returned unchanged by identity.
 */
export const repointBranchRefs = (questions, fromId, toId) => {
  if (!fromId || !toId || fromId === toId) return questions || [];
  let changed = false;

  const walk = (cond) => {
    if (!cond || typeof cond !== "object") return cond;
    if (Array.isArray(cond.all)) return { ...cond, all: cond.all.map(walk) };
    if (Array.isArray(cond.any)) return { ...cond, any: cond.any.map(walk) };
    if (cond.not) return { ...cond, not: walk(cond.not) };
    if (cond.questionId === fromId) {
      changed = true;
      return { ...cond, questionId: toId };
    }
    return cond;
  };

  const out = (questions || []).map((q) => {
    if (!q || !q.visibleWhen) return q;
    const before = changed;
    const next = walk(q.visibleWhen);
    return changed !== before ? { ...q, visibleWhen: next } : q;
  });

  return changed ? out : questions || [];
};

export const makeColumn = (init = {}) => {
  const type = normFieldType(init.type);
  const col = {
    id: init.id || newColumnId(),
    label: String(init.label || "").slice(0, 200),
    type,
  };
  if (type === "Select" || type === "MultiSelect") {
    // Same generous ceiling as a question's own options — see makeQuestion.
    col.options = (Array.isArray(init.options) ? init.options : [])
      .map((o) => String(o))
      .slice(0, 2000);
  }
  if (type === "Number" && init.unit) col.unit = String(init.unit).slice(0, 40);
  // Share of the table's data-column area, as a percent. STRUCTURE, not an
  // answer, so it survives stripAnswers like rowLabels does. Only meaningful
  // when EVERY column carries one (see columnWidthPercents) — a partially
  // sized table falls back to equal shares, which is what every table did
  // before this existed. Absent unless asked for, so nothing about an
  // existing table changes.
  const width = Number(init.width);
  if (Number.isFinite(width) && width > 0 && width <= 100) col.width = width;

  // A second header line under the column's name, printed smaller and NOT
  // bold: the date format on "End Date", the "If yes, list name" prompt under
  // "Antibiotics (Y/N)". Distinct from `unit`, which is a unit of measure
  // printed inline and grey — this is a full line of its own, and it is what
  // stops a long header from reading as one run-on phrase.
  if (init.sublabel) col.sublabel = String(init.sublabel).slice(0, 200);

  // Columns carrying the same `group` are drawn under ONE spanning header cell
  // named by it, with their own names on a second header line beneath — the
  // paper form's "Corticoids" over "# of injections" / "# of days". Only
  // consecutive columns group; see columnHeaderGroups.
  if (init.group) col.group = String(init.group).slice(0, 200);

  // Print this column's choices stacked one per line rather than run together
  // on one. A two-choice cell ("Ongoing" / "Resolved") reads as a pair of
  // options stacked; side by side in a narrow cell it wraps arbitrarily.
  if (init.stack) col.stack = true;

  // This column's prefilled text is PART OF THE FORM, not a pre-entered answer.
  //
  // An ordinary prefill prints bold, because it is a default someone typed into
  // an otherwise blank field and the reader has to be able to tell it apart from
  // the printed form around it. The eligibility checklist inverts that: the
  // criterion text IS the form — a whole page of it — and printing it bold says
  // every line was pre-answered. Marked structural, it prints in ordinary
  // weight, exactly like a column header or a row label.
  //
  // Absent on every column that predates the checklist, so nothing already
  // saved changes. See tableCellHtml / tableCellRuns.
  if (init.structural) col.structural = true;

  // One word of this column's HEADER printed bold and underlined.
  //
  // "Inclusion Criteria (must be yes to qualify)" and "Exclusion Criteria (must
  // be no to qualify)" are the two headings on the eligibility checklist, and
  // they are opposites that differ by one word. On a page of identical tick
  // boxes that word is the entire instruction, so the paper form emphasises it —
  // reading the exclusion grid as though it were the inclusion grid is the one
  // mistake this document exists to prevent.
  //
  // A single word rather than markup in the label, because the label is also
  // the question text, the editor's column name and the .docx header — plain
  // text everywhere else. Matched whole-word and case-insensitively; absent
  // unless asked for, so no other column's header changes.
  if (init.emphasize) col.emphasize = String(init.emphasize).slice(0, 40);
  return col;
};

/**
 * Split a column header around the word it wants emphasised.
 *
 * Returns `[before, match, after]`, or null when there is nothing to emphasise
 * (no `emphasize`, or the word isn't in the label). Shared by both exporters so
 * the Word file and the printed page can't disagree about which word is marked
 * — the same reason columnPrintsBoxes and choiceMarkFor live in this file.
 *
 * @param {object} column
 * @returns {[string, string, string]|null}
 */
/** A protocol's own sub-part label at the head of a line: "a.", "(b)", "iii.",
 *  "•", "-", "2)". The marker stays part of the text — it is the protocol's,
 *  not ours to renumber. */
const SUBPART_MARKER =
  /^(?:[([]?[a-z][).\]]|[([]?(?:i{1,3}|iv|vi{0,3}|ix|xi{0,2})[).\]]|\d{1,2}[).\]]|[-•·*–—])\s+/i;

/**
 * Split a criterion into the lines it should print as, or null for a plain
 * one-line criterion.
 *
 * A protocol rarely writes a criterion as one paragraph. It writes a lead-in
 * sentence and then sets its carve-outs out on their own indented lines:
 *
 *     Any history of malignancy within 5 years, except:
 *       a. adequately treated basal cell carcinoma
 *       b. carcinoma in situ of the cervix
 *
 * That layout is not decoration — it is what a monitor's eye follows when
 * checking the printed form against the protocol, and flattening it into one
 * run-on paragraph made both harder to read. The extraction preserves the
 * protocol's own newlines and its own sub-part markers ("a.", "(b)", "i.", "•");
 * this turns them back into lines at print time.
 *
 * Returns null — not a one-element array — when there is nothing to lay out, so
 * every renderer keeps its existing single-line path untouched for the ordinary
 * case. Shared from here so the .docx and the printed page indent the same
 * lines, the same reason emphasisParts lives here.
 *
 * @param {string} text
 * @returns {Array<{text: string, indent: boolean}>|null}
 */
export const criterionLines = (text) => {
  const lines = String(text == null ? "" : text)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length <= 1) return null;

  // Only lay it out when the break really is STRUCTURE. A model asked for
  // verbatim text can still wrap a long sentence, and indenting the tail of a
  // wrapped sentence invents a sub-part the protocol never had — worse than
  // printing the paragraph it used to print. So require evidence: either a line
  // carrying the protocol's own sub-part marker, or a lead-in that announces a
  // list by ending in a colon. Anything else is re-joined and prints as one
  // paragraph, exactly as before.
  const marked = lines
    .slice(1)
    .some((l) => SUBPART_MARKER.test(l));
  if (!marked && !/:$/.test(lines[0])) return null;

  // Everything after the lead-in is a sub-part, and takes one indent. Depth is
  // deliberately flat: the protocol's own marker already says which level a
  // line is, and guessing a second level from it would invent structure the
  // document does not have.
  return lines.map((line, i) => ({ text: line, indent: i > 0 }));
};

export const emphasisParts = (column) => {
  const label = String(column?.label || "");
  const word = String(column?.emphasize || "").trim();
  if (!word || !label) return null;
  // Whole word only: the "no" in "must be no to qualify", never the "no" inside
  // "Exclusion".
  const re = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`, "i");
  const m = re.exec(label);
  if (!m) return null;
  return [label.slice(0, m.index), m[0], label.slice(m.index + m[0].length)];
};

/**
 * Group consecutive columns that share a `group` into spanning header cells.
 *
 * Returns null when no column carries one, so a table without groups keeps the
 * single header row it has always had and neither renderer changes. Otherwise
 * returns [{ label, span, from }], left to right, covering every column —
 * an ungrouped column is its own run of one with a null label, which the
 * renderers draw as a cell spanning BOTH header rows.
 *
 * Only CONSECUTIVE columns group: two runs with the same name stay two cells,
 * because a spanning header that jumped a column in between would claim
 * columns it does not describe.
 *
 * @param {Array<object>} columns
 * @returns {Array<{label: string|null, span: number, from: number}>|null}
 */
export const columnHeaderGroups = (columns) => {
  const cols = Array.isArray(columns) ? columns : [];
  if (!cols.some((c) => c && c.group)) return null;
  const runs = [];
  cols.forEach((c, i) => {
    const g = (c && c.group) || null;
    const last = runs[runs.length - 1];
    if (g && last && last.label === g) last.span += 1;
    else runs.push({ label: g, span: 1, from: i });
  });
  return runs;
};

/**
 * Which mark a choice list prints: "radio" (round, pick one) or "box" (square,
 * tick any). Normally decided by type — see choiceMarkFor, and the paper
 * convention it encodes.
 *
 * `markStyle` overrides it for the case that convention can't cover: a printed
 * form that draws a pick-ONE question with square boxes anyway. The source
 * document being replaced is the authority on how it looks, so this exists to
 * match it — but it is deliberately an explicit per-question opt-in, never a
 * default, so the convention still holds everywhere nobody has said otherwise.
 *
 * @param {object} q a question or a table column
 * @returns {"radio"|"box"}
 */
export const markStyleFor = (q) =>
  q?.markStyle === "box" || q?.markStyle === "radio"
    ? q.markStyle
    : choiceMarkFor(q?.type);

export const makeEvent = (init = {}) => ({
  id: init.id || newEventId(),
  eventName: String(init.eventName || "").slice(0, 300),
  questions: (Array.isArray(init.questions) ? init.questions : []).map((q) =>
    makeQuestion(q)
  ),
  notes: init.notes || "",
  signatures: init.signatures || defaultEventSignatures(),
  ...(init.appliesTo && init.appliesTo !== "all"
    ? { appliesTo: init.appliesTo }
    : {}),
  ...(init.visitId ? { visitId: init.visitId } : {}),
  // A standing LOG (con meds, medical history) rather than a visit. It is an
  // ordinary event in every other respect — approval, versioning, export and
  // the patient's fill-in all work on it unchanged — but it prints with log
  // chrome: a blank "Page __ of __" because it is ongoing, and one
  // investigator sign-off line instead of the per-visit closing block.
  // The flag lives on the EVENT, not the section, because the event is what
  // the two exporters are handed; a section can't be asked.
  // The eligibility checklist. Same idea as "log" above and carried the same
  // way — see eventIsCriteria for how the two differ on the page.
  ...(init.kind === "log" || init.kind === "criteria" ? { kind: init.kind } : {}),
  ...(init.orientation === "landscape" ? { orientation: "landscape" } : {}),
  // A visit is approved on its own. It has to survive every rebuild through
  // this factory — normalizeTemplate runs every event back through it on each
  // save, so dropping these here would un-approve the whole template the next
  // time anybody typed. Absent means draft; nothing is written for a visit
  // that has never been approved.
  ...(init.status === "approved" ? { status: "approved" } : {}),
  ...(init.approvedAt ? { approvedAt: String(init.approvedAt) } : {}),
  ...(init.approvedBy ? { approvedBy: makeVersionActor(init.approvedBy) } : {}),
  // "This visit's source was finalized on PAPER" — see eventIsPaperFinal.
  // Carried through the factory for the same reason the approval fields are:
  // every save runs each event back through here, so a field the factory
  // doesn't know about is a field that disappears the next time anyone types.
  ...(init.paperFinal
    ? {
        paperFinal: true,
        ...(init.paperFinalAt ? { paperFinalAt: String(init.paperFinalAt) } : {}),
        ...(init.paperFinalBy
          ? { paperFinalBy: makeVersionActor(init.paperFinalBy) }
          : {}),
      }
    : {}),
});

/** A visit that has been signed off on its own. */
export const eventIsApproved = (event) => event?.status === "approved";

/**
 * Whether this document's authoritative copy is a PAPER one.
 *
 * SourceX arrived mid-study. A site can easily have Screening finalized on
 * paper — printed, filled, signed, filed — and be using SourceX from Visit 2
 * on. Nothing in the template says so, so the next coordinator exports
 * Screening from here, files it, and the study now has two source documents
 * for one visit.
 *
 * This is a MARKER, not a lock: it changes nothing about the document, blocks
 * no export, and is not part of approval. It records that somebody knows where
 * the real copy lives, and who to ask for it.
 */
export const eventIsPaperFinal = (event) => event?.paperFinal === true;

/** Every visit in a template, flattened — the unit approval now works in. */
export const allEvents = (sections = []) =>
  (Array.isArray(sections) ? sections : []).flatMap((s) =>
    Array.isArray(s?.events) ? s.events : []
  );

/**
 * The template's own status, DERIVED from its visits: approved only once every
 * visit is. It is not set directly any more — a template is exactly as approved
 * as its least-approved visit, and anything that reads `template.status`
 * (patient enrolment's "this is still a draft" warning, the Firestore rule that
 * locks an approved template to approvers) must not be told otherwise.
 */
export const templateStatusFromSections = (sections = []) => {
  const events = allEvents(sections);
  return events.length && events.every(eventIsApproved) ? "approved" : "draft";
};

/** How many visits are signed off, and how many there are. */
export const approvalProgress = (sections = []) => {
  const events = allEvents(sections);
  return { approved: events.filter(eventIsApproved).length, total: events.length };
};

/**
 * Back-fill per-visit approval onto a template that was approved BEFORE
 * approval was per visit.
 *
 * Without this, the very first save of such a template would derive its status
 * from visits that carry none, find zero approved, and silently demote a live
 * template — the one patients are being enrolled against — to a draft. The old
 * template-level `status: "approved"` is the record that every visit in it was
 * signed off, so that is exactly what gets written onto them.
 *
 * A no-op the moment any visit carries its own status, so it can be called on
 * every load without re-stamping anything.
 */
export const backfillEventApproval = (template) => {
  const t = template && typeof template === "object" ? template : {};
  const events = allEvents(t.sections);
  if (t.status !== "approved" || !events.length || events.some(eventIsApproved)) {
    return t;
  }
  return {
    ...t,
    sections: (t.sections || []).map((s) => ({
      ...s,
      events: (s.events || []).map((e) => ({
        ...e,
        status: "approved",
        ...(t.approvedAt ? { approvedAt: t.approvedAt } : {}),
        ...(t.approvedBy ? { approvedBy: t.approvedBy } : {}),
      })),
    })),
  };
};

/**
 * Structural problems in ONE visit, so an unfinished visit elsewhere can't hold
 * up approving this one. Runs the same checks as validateTemplate against a
 * single-visit slice, then adds back the one template-wide problem that
 * genuinely implicates this visit: a question id it shares with another visit,
 * which corrupts patient data for both.
 *
 * @param {object} template
 * @param {string} eventId
 * @returns {string[]}
 */
export const validateEvent = (template, eventId) => {
  const slice = sectionsForEvent(template?.sections, eventId);
  if (!slice.length) return ["That visit is no longer part of this template."];

  const problems = validateTemplate({ ...template, sections: slice });

  const mine = new Set();
  allEvents(slice).forEach((e) =>
    (e?.questions || []).forEach((q) => q?.id && mine.add(q.id))
  );
  const shared = new Set();
  allEvents(template?.sections).forEach((e) => {
    if (e?.id === eventId) return;
    (e?.questions || []).forEach((q) => {
      if (q?.id && mine.has(q.id)) shared.add(q.id);
    });
  });
  shared.forEach((id) =>
    problems.push(
      `Question id ${id} is also used in another visit — ids must be unique across the whole template.`
    )
  );
  return problems;
};

export const makeSection = (init = {}) => ({
  id: init.id || newSectionId(),
  name: String(init.name || "").slice(0, 200),
  // Marks the section the editor's section dropdown shows as a log, or as the
  // eligibility checklist, rather than a group of visits. Purely for the UI —
  // the exporters read the flag on the EVENT, which is the authority. Both
  // kinds hold exactly ONE event, which is what the editor uses this for: it
  // hides the visit picker and selects that event automatically. See makeEvent.
  ...(init.kind === "log" || init.kind === "criteria" ? { kind: init.kind } : {}),
  events: (Array.isArray(init.events) ? init.events : []).map((e) =>
    makeEvent(e)
  ),
});

/** Header identifier fields that appear at the top of every printed source doc. */
export const defaultHeaderFields = () => [
  { id: "hdr_protocol", label: "Protocol #", type: "Text" },
  { id: "hdr_site", label: "Site #", type: "Text" },
  { id: "hdr_pi", label: "Principal Investigator", type: "Text" },
  { id: "hdr_patient", label: "Patient #", type: "Text" },
  { id: "hdr_initials", label: "Patient Initials", type: "Text" },
  { id: "hdr_visit_date", label: "Visit Date", type: "Date" },
];

// ---------------------------------------------------------------------------
// Normalization / migration (v1 -> v2, in memory, idempotent)
// ---------------------------------------------------------------------------

/**
 * Upgrade a stored template (or a patient's snapshot copy) to the v2 shape
 * without mutating the input and without dropping unknown fields.
 *
 * Idempotent: normalize(normalize(x)) deep-equals normalize(x).
 * Order-preserving: never reorders sections/events/questions, because legacy
 * patient answers are addressed positionally.
 *
 * @param {object} raw a sourcex_templates doc, or a patient doc's section array holder
 * @returns {object} normalized copy
 */
export const normalizeTemplate = (raw) => {
  const t = raw && typeof raw === "object" ? raw : {};
  const sections = Array.isArray(t.sections) ? t.sections : [];
  return {
    ...t,
    schemaVersion: TEMPLATE_SCHEMA_VERSION,
    headerFields:
      Array.isArray(t.headerFields) && t.headerFields.length
        ? t.headerFields
        : defaultHeaderFields(),
    sections: sections.map((s, si) => normalizeSection(s, si)),
  };
};

/**
 * Normalize the `data` array on a `sourcex_patients` doc — the same section
 * shape, but it carries real answers, so values are preserved verbatim.
 * @param {Array} data
 * @returns {Array}
 */
export const normalizePatientData = (data) =>
  (Array.isArray(data) ? data : []).map((s, si) => normalizeSection(s, si));

const normalizeSection = (rawSection, sectionIndex) => {
  const s = rawSection && typeof rawSection === "object" ? rawSection : {};
  const name = s.name || "";
  return {
    ...s,
    id: s.id || legacySectionId(name, sectionIndex),
    name,
    events: (Array.isArray(s.events) ? s.events : []).map((e, ei) =>
      normalizeEvent(e, name, ei)
    ),
  };
};

const normalizeEvent = (rawEvent, sectionName, eventIndex) => {
  const e = rawEvent && typeof rawEvent === "object" ? rawEvent : {};
  const eventName = e.eventName || "";
  return {
    ...e,
    id: e.id || legacyEventId(sectionName, eventName, eventIndex),
    eventName,
    notes: e.notes || "",
    signatures: e.signatures || defaultEventSignatures(),
    // Question ids must be unique WITHIN an event — answers are keyed by id, so
    // two questions sharing one cannot both be answered. A duplicate gets a
    // fresh id here rather than being left to fail validation.
    //
    // This happens when a block is copied with its ids and a copy is later
    // relabelled: "Spirometry (pre-BD)" and "Spirometry (post-BD)" are plainly
    // different questions, yet carried one id and were reported as colliding,
    // which blocked approval with no way forward from inside the editor.
    //
    // Re-minting the LATER occurrence is safe: while two questions shared an
    // id, only one of them could hold an answer at all, so there is no
    // second set of answers to strand.
    questions: (() => {
      const seenIds = new Set();
      return (Array.isArray(e.questions) ? e.questions : []).map((q, qi) => {
        const out = normalizeQuestion(q, sectionName, eventName, qi);
        if (seenIds.has(out.id)) {
          let salt = 1;
          let candidate;
          do {
            candidate = legacyQuestionId(
              sectionName,
              eventName,
              qi,
              `${out.question || ""}#dup${salt}`
            );
            salt += 1;
          } while (seenIds.has(candidate));
          out.id = candidate;
        }
        seenIds.add(out.id);
        return out;
      });
    })(),
  };
};

const normalizeQuestion = (rawQ, sectionName, eventName, index) => {
  const q = rawQ && typeof rawQ === "object" ? rawQ : {};
  // Flattened on the way IN, so every reader downstream — the editor, the
  // outline, the exports, the condition pickers — sees a plain string and
  // none of them has to know the rich-text shape exists. See labelToPlainText.
  const label = labelToPlainText(q.question);
  const type = normFieldType(q.type);
  const out = {
    ...q,
    id: q.id || legacyQuestionId(sectionName, eventName, index, label),
    question: label,
    type,
  };

  // Keep the styled original alongside the flattened text. A save from THIS
  // branch writes the whole normalized template back, so without this the
  // first edit anywhere in the visit would quietly overwrite the run array
  // with plain text and the formatting would be gone for the branch that can
  // render it. `questionRuns` is not a field this branch reads — it exists so
  // the styling is recoverable rather than destroyed in passing.
  if (Array.isArray(q.question) && !out.questionRuns) {
    out.questionRuns = q.question;
  }

  // Preserve an existing answer exactly; only supply a default when absent, so
  // normalizing a patient's in-progress doc can never discard data.
  if (out.value === undefined || out.value === null) {
    out.value = emptyValueFor(type);
  }

  // Coerce answers whose container type changed shape (v1 stored everything as
  // a scalar). A non-empty scalar becomes a single-element array rather than
  // being thrown away.
  if (type === "MultiSelect" && !Array.isArray(out.value)) {
    out.value = isBlankValue(out.value) ? [] : [out.value];
  }
  if (type === "Table" && !Array.isArray(out.value)) {
    out.value = [];
  }

  if (type === "Select" || type === "MultiSelect") {
    out.options = Array.isArray(q.options) ? q.options.map((o) => String(o)) : [];
  }
  if (type === "Table") {
    out.columns = (Array.isArray(q.columns) ? q.columns : []).map((c, ci) => ({
      ...c,
      id: c && c.id ? c.id : `c_${stableHash(`${label}|${ci}|${c?.label || ""}`)}`,
      label: (c && c.label) || "",
      type: normFieldType(c && c.type),
    }));
  }
  if (type === "Number" && Array.isArray(q.units) && q.units.length) {
    out.units = q.units.map((u) => String(u));
    out.unit = q.unit && out.units.includes(q.unit) ? q.unit : out.units[0];
  }

  return out;
};

// ---------------------------------------------------------------------------
// PHI guard
// ---------------------------------------------------------------------------

/**
 * Return a copy of a template with every answer cleared.
 *
 * A `sourcex_templates` doc is shared by every patient in the study and has no
 * per-patient audit trail, so a clinical value typed into the template editor
 * would (a) persist to every patient enrolled afterward and (b) sit in a
 * collection nobody treats as PHI-bearing. Call this on the way IN to any
 * template write — the editor's inputs are a preview of the form, not a place
 * to record data. Answers belong in `sourcex_patients` only.
 *
 * Table/MultiSelect reset to [], Checkbox to false, everything else to "".
 * @param {object} template
 * @returns {object}
 */
const stripSections = (sections) =>
  (Array.isArray(sections) ? sections : []).map((s) => ({
    ...s,
    events: (Array.isArray(s.events) ? s.events : []).map((e) => ({
      ...e,
      questions: (Array.isArray(e.questions) ? e.questions : []).map((q) => ({
        ...q,
        value: emptyValueFor(q && q.type),
      })),
    })),
  }));

export const stripAnswers = (template) => {
  const t = template && typeof template === "object" ? template : {};
  return {
    ...t,
    sections: stripSections(t.sections),
    // Defense in depth: every version snapshot is already answer-free at the
    // moment it's created (see makeTemplateVersion), but a template this old
    // guard runs on is the one place EVERY write funnels through, so cleaning
    // versionHistory here too means a PHI leak can never ride along even if
    // some future write path builds a snapshot a different way.
    ...(Array.isArray(t.versionHistory)
      ? {
          versionHistory: t.versionHistory.map((v) => ({
            ...v,
            sections: stripSections(v?.sections),
          })),
        }
      : {}),
  };
};

/**
 * The single funnel every write to `sourcex_templates` should pass through:
 * normalize to v2 (assigning stable ids and stamping schemaVersion), then
 * strip any answers. Use this rather than calling the two separately, so a new
 * write path can't accidentally get one half of the guarantee.
 * @param {object} template
 * @returns {object}
 */
export const prepareTemplateForWrite = (template) =>
  stripAnswers(normalizeTemplate(template));

// ---------------------------------------------------------------------------
// Version history
//
// A coordinator's edits live only in `sections` — there was nothing stopping
// a re-run of the AI draft (GenerateTemplateModal) from silently overwriting
// hand-made changes, or an approval from being un-recoverable once
// superseded. Every "rebuild from scratch" and every approve/re-approve now
// snapshots the content it's about to replace first, so it can always be
// reviewed or restored later.
//
// Snapshots are stored as their own Firestore documents, in a `versions`
// SUBCOLLECTION of the template (see getTemplateVersionsPath in
// firestorePaths.js and templateVersionService.js for the read/write side) —
// NOT as a field on the template document itself. An earlier version of this
// feature embedded them as an array field, which meant a template's own
// content and every past snapshot combined shared ONE document's 1 MiB
// Firestore limit; a template with a handful of full-content snapshots behind
// it could push the live document over that limit and make ordinary saves
// (including approval, which always adds one more snapshot) fail outright.
// Only the snapshot-shaping helper lives here — it has no Firebase/I-O
// dependency, matching this file's framework-free design constraint.
// ---------------------------------------------------------------------------
export const MAX_TEMPLATE_VERSIONS = 20;

export const newVersionId = () => newId("v");

/**
 * One version snapshot. `sections` is cleaned through stripAnswers on the way
 * in, so a snapshot is exactly as PHI-safe as the live template — a version
 * is a record of STRUCTURE, never of a patient's answers.
 *
 * A snapshot covers ONE VISIT. A visit is the unit a coordinator actually
 * works on and the unit that exports as a document, so "the version I want
 * back" always meant one visit's worth of content — while restoring a
 * whole-template snapshot to get it silently rewrote every other visit too.
 * Pass `eventId`/`eventName` and `sections` narrowed to that one visit.
 *
 * Omitting `eventId` still produces a whole-template snapshot. That is what
 * every version written before this existed is, and what a rebuild that
 * replaces the entire template still legitimately needs.
 *
 * @param {object} args
 * @param {"built"|"approved"|"backup"} args.kind  what produced this snapshot —
 *   "built" (about to be replaced by a rebuild), "approved" (just approved),
 *   or "backup" (about to be replaced by restoring an older version)
 * @param {string} [args.label]  short human note, e.g. which visit/protocol
 * @param {Array} args.sections  the template content being replaced/frozen
 * @param {string} [args.eventId]  the visit this snapshot covers
 * @param {string} [args.eventName]  that visit's name, kept so the history
 *   reads correctly even after the visit is renamed or deleted
 * @param {object} [args.actor]  who caused it — { email, name, initials }
 * @returns {object}
 */
export const makeTemplateVersion = ({
  kind,
  label = "",
  sections,
  eventId = "",
  eventName = "",
  actor = null,
}) => ({
  id: newVersionId(),
  // A client timestamp, deliberately — consistent whether written from the
  // browser (firebase/firestore) or a Cloud Function (Admin SDK), and easy to
  // orderBy() without waiting on server resolution.
  createdAt: new Date().toISOString(),
  kind,
  label: String(label || "").slice(0, 200),
  sections: stripSections(sections),
  ...(eventId ? { eventId: String(eventId), eventName: String(eventName || "") } : {}),
  ...(actor ? { actor: makeVersionActor(actor) } : {}),
});

/** Who a version or an audit event is attributed to. Email is the identity
 * that is always known; name and initials are best-effort (see
 * approverIdentity.js) and are recorded blank rather than guessed at. */
export const makeVersionActor = ({ email = "", name = "", initials = "" } = {}) => ({
  email: String(email || "").slice(0, 200),
  name: String(name || "").slice(0, 120),
  initials: String(initials || "").slice(0, 10),
});

/**
 * Which visit a version covers, or "" for a whole-template snapshot.
 * Centralised so the "is this one of the old whole-template ones?" test is
 * asked the same way everywhere.
 * @param {object} version
 * @returns {string}
 */
export const versionEventId = (version) => String(version?.eventId || "");

/** The single visit inside a visit-scoped snapshot, or null. */
export const versionEvent = (version) => {
  if (!versionEventId(version)) return null;
  for (const s of version?.sections || []) {
    for (const ev of s?.events || []) {
      if (ev?.id === version.eventId) return ev;
    }
  }
  // The snapshot names a visit but doesn't contain it — treat as empty rather
  // than as "whole template", which would restore the wrong thing.
  return null;
};

/**
 * Narrow a whole template's `sections` to just one visit, preserving the
 * section it lives under so a restore knows where to put it back.
 * @param {Array} sections
 * @param {string} eventId
 * @returns {Array} a one-section, one-event slice (empty if not found)
 */
export const sectionsForEvent = (sections = [], eventId = "") => {
  if (!eventId) return sections;
  for (const s of sections) {
    const ev = (s?.events || []).find((e) => e?.id === eventId);
    if (ev) return [{ ...s, events: [ev] }];
  }
  return [];
};

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Structural validation — catches a malformed template before it is written or
 * approved. Returns human-readable problems; empty means structurally usable.
 * Deliberately permissive about content (that is the coordinator's review job)
 * and strict about anything that would corrupt data addressing.
 * @param {object} template
 * @returns {string[]}
 */
export const validateTemplate = (template) => {
  const problems = [];
  if (!template || typeof template !== "object") {
    return ["Template is missing or not an object."];
  }
  if (!Array.isArray(template.sections)) {
    return ["template.sections is not an array."];
  }

  const seenQuestionIds = new Map(); // id -> "section / event / label"
  const allQuestionIds = new Set();

  template.sections.forEach((s, si) => {
    const sLabel = s?.name || `section[${si}]`;
    if (!s || typeof s !== "object") {
      problems.push(`${sLabel} is not an object.`);
      return;
    }
    if (!s.name || !String(s.name).trim()) {
      problems.push(`section[${si}] has no name.`);
    }
    (Array.isArray(s.events) ? s.events : []).forEach((e, ei) => {
      const eLabel = e?.eventName || `event[${ei}]`;
      if (!e?.eventName || !String(e.eventName).trim()) {
        problems.push(`${sLabel} / event[${ei}] has no eventName.`);
      }
      (Array.isArray(e?.questions) ? e.questions : []).forEach((q, qi) => {
        const where = `${sLabel} / ${eLabel} / question[${qi}]`;
        if (!q || typeof q !== "object") {
          problems.push(`${where} is not an object.`);
          return;
        }
        if (!q.question || !String(q.question).trim()) {
          problems.push(`${where} has no question text.`);
        }
        if (!q.id) {
          problems.push(`${where} has no id (run normalizeTemplate first).`);
        } else {
          allQuestionIds.add(q.id);
          if (seenQuestionIds.has(q.id)) {
            problems.push(
              `${where} has duplicate id "${q.id}" (also at ${seenQuestionIds.get(
                q.id
              )}) — answers would collide.`
            );
          } else {
            seenQuestionIds.set(q.id, where);
          }
        }
        if (!FIELD_TYPES.includes(q.type)) {
          problems.push(`${where} has unknown type "${q.type}".`);
        }
        if (
          (q.type === "Select" || q.type === "MultiSelect") &&
          (!Array.isArray(q.options) || q.options.length === 0)
        ) {
          problems.push(`${where} is "${q.type}" but has no options.`);
        }
        if (
          q.type === "Table" &&
          (!Array.isArray(q.columns) || q.columns.length === 0)
        ) {
          problems.push(`${where} is a Table but has no columns.`);
        }
      });
    });
  });

  // Branching integrity — a condition pointing at a nonexistent question would
  // hide the field forever (or reveal it always), silently.
  collectConditions(template).forEach(({ cond, where }) => {
    if (cond.questionId && !allQuestionIds.has(cond.questionId)) {
      problems.push(
        `${where} branches on unknown question id "${cond.questionId}".`
      );
    }
    if (cond.op && !CONDITION_OPS.includes(cond.op)) {
      problems.push(`${where} uses unknown condition operator "${cond.op}".`);
    }
  });

  return problems;
};

/**
 * Which questions have a structural problem, keyed by question id.
 *
 * A UI-facing companion to validateTemplate: that returns human-readable
 * sentences for the approval banner, this returns a Map so the editor can
 * point at the exact field (and its heading) in red. The field-level rules are
 * intentionally the SAME set validateTemplate checks — no id, no text, unknown
 * type, empty Select/MultiSelect, column-less Table, duplicate id, and a
 * branch that targets a question that isn't in the template.
 *
 * @param {object} template
 * @returns {Map<string, string[]>} questionId -> problem messages
 */
export const collectQuestionProblems = (template) => {
  const byId = new Map();
  if (!template || !Array.isArray(template.sections)) return byId;

  const add = (id, msg) => {
    if (!id) return;
    if (!byId.has(id)) byId.set(id, []);
    byId.get(id).push(msg);
  };

  const seen = new Map(); // id -> true, for duplicate detection
  const allQuestionIds = new Set();

  template.sections.forEach((s) => {
    (Array.isArray(s?.events) ? s.events : []).forEach((e) => {
      (Array.isArray(e?.questions) ? e.questions : []).forEach((q) => {
        if (!q || typeof q !== "object") return;
        if (q.id) allQuestionIds.add(q.id);
        if (!q.question || !String(q.question).trim()) {
          add(q.id, "Missing question text.");
        }
        if (q.id && seen.has(q.id)) {
          add(q.id, "Duplicate question id — answers would collide.");
        } else if (q.id) {
          seen.set(q.id, true);
        }
        if (!FIELD_TYPES.includes(q.type)) {
          add(q.id, `Unknown field type "${q.type}".`);
        }
        if (
          (q.type === "Select" || q.type === "MultiSelect") &&
          (!Array.isArray(q.options) || q.options.length === 0)
        ) {
          add(q.id, `"${q.type}" has no options.`);
        }
        if (
          q.type === "Table" &&
          (!Array.isArray(q.columns) || q.columns.length === 0)
        ) {
          add(q.id, "Table has no columns.");
        }
      });
    });
  });

  // A field whose branch points at a question that doesn't exist.
  template.sections.forEach((s) => {
    (Array.isArray(s?.events) ? s.events : []).forEach((e) => {
      (Array.isArray(e?.questions) ? e.questions : []).forEach((q) => {
        const leaves = [];
        const walk = (c) => {
          if (!c || typeof c !== "object") return;
          if (Array.isArray(c.all)) return c.all.forEach(walk);
          if (Array.isArray(c.any)) return c.any.forEach(walk);
          if (c.not) return walk(c.not);
          leaves.push(c);
        };
        if (q?.visibleWhen) walk(q.visibleWhen);
        leaves.forEach((c) => {
          if (c.questionId && !allQuestionIds.has(c.questionId)) {
            add(q.id, `Branches on a question that no longer exists.`);
          }
        });
      });
    });
  });

  return byId;
};

/** Flatten every leaf condition in a template, with a location label. */
const collectConditions = (template) => {
  const out = [];
  const walk = (cond, where) => {
    if (!cond || typeof cond !== "object") return;
    if (Array.isArray(cond.all)) return cond.all.forEach((c) => walk(c, where));
    if (Array.isArray(cond.any)) return cond.any.forEach((c) => walk(c, where));
    if (cond.not) return walk(cond.not, where);
    out.push({ cond, where });
  };
  (template?.sections || []).forEach((s) =>
    (s?.events || []).forEach((e) =>
      (e?.questions || []).forEach((q, qi) => {
        if (q?.visibleWhen) {
          walk(
            q.visibleWhen,
            `${s?.name} / ${e?.eventName} / question[${qi}] "${q.question}"`
          );
        }
      })
    )
  );
  return out;
};

// ---------------------------------------------------------------------------
// Applicability filtering (roadmap Phase 3 — code, no AI)
// ---------------------------------------------------------------------------

/**
 * Drop questions that don't apply to this study configuration (adult vs
 * adolescent site, optional sub-studies). Pure filter over JSON — no tokens.
 * @param {object} template
 * @param {{population?: "adult"|"adolescent"|"both", includeOptional?: boolean}} config
 * @returns {object} filtered copy
 */
export const filterByApplicability = (template, config = {}) => {
  const population = config.population || "both";
  const includeOptional = config.includeOptional !== false;

  const keep = (appliesTo) => {
    const a = appliesTo || "all";
    if (a === "all" || a === "unknown") return true;
    if (a === "optional") return includeOptional;
    if (a === "adults_only") return population !== "adolescent";
    if (a === "adolescents_only") return population !== "adult";
    return true;
  };

  const t = template && typeof template === "object" ? template : {};
  return {
    ...t,
    sections: (Array.isArray(t.sections) ? t.sections : []).map((s) => ({
      ...s,
      events: (Array.isArray(s.events) ? s.events : [])
        .filter((e) => keep(e?.appliesTo))
        .map((e) => ({
          ...e,
          questions: (Array.isArray(e.questions) ? e.questions : []).filter(
            (q) => keep(q?.appliesTo)
          ),
        })),
    })),
  };
};

/** Count questions in a template (source-only blocks excluded). */
export const countQuestions = (template) =>
  (template?.sections || []).reduce(
    (n, s) =>
      n +
      (s?.events || []).reduce(
        (m, e) =>
          m +
          (e?.questions || []).filter((q) => !isSourceOnlyType(q?.type)).length,
        0
      ),
    0
  );
